import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Sparkles, MailCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function PatientRegister() {
  const { slug } = useParams();
  const { setSessionUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);

  const [step, setStep] = useState("form"); // form | otp
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "", confirm_password: "",
    phone: "", date_of_birth: "",
    referral_code: params.get("ref") || "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/tenants/by-slug/${slug}`).then(({ data }) => setTenant(data)).catch(() => {});
  }, [slug]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startRegistration = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { confirm_password, ...payload } = form;
      const { data } = await api.post("/auth/register/start", {
        ...payload, tenant_slug: slug, role: "patient",
      });
      setVerificationId(data.verification_id);
      setStep("otp");
      setResendIn(30);
      toast.success(`Verification code sent to ${data.email}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to send code"));
    } finally { setSubmitting(false); }
  };

  const verify = async () => {
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register/verify", { verification_id: verificationId, code: otp });
      setSessionUser(data.token, data.user);
      toast.success(`Welcome to ${tenant?.name || "the clinic"}!`);
      navigate(`/c/${slug}/dashboard`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Incorrect code"));
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      const { data } = await api.post("/auth/register/resend", { verification_id: verificationId });
      setVerificationId(data.verification_id);
      setOtp("");
      setResendIn(30);
      toast.success("New code sent");
    } catch { toast.error("Could not resend"); }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("mr_pending_tenant_slug", slug);
    const redirectUrl = window.location.origin + `/c/${slug}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-mr-bg flex items-center justify-center p-6 lg:p-10">
      <div className="w-full max-w-xl bg-white border border-mr rounded-3xl p-8 lg:p-10 shadow-sm">
        <Link to={`/c/${slug}`} className="inline-flex items-center gap-2 mb-8" data-testid="register-back-link">
          <div className="h-8 w-8 rounded-lg bg-mr-primary text-white flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>
          <span className="font-display font-semibold text-mr-primary">{tenant?.name || "MedRewards"}</span>
        </Link>

        {step === "form" && (
          <>
            <h1 className="font-display text-3xl font-semibold text-mr-primary">Join the programme</h1>
            <p className="text-mr-secondary mt-2">We'll email you a 6-digit code to verify your address.</p>

            <form onSubmit={startRegistration} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Full name</Label>
                <Input data-testid="register-name-input" required value={form.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Amir Khalifa" className="mt-2 rounded-xl bg-mr-bg border-mr h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label>Email</Label>
                <Input data-testid="register-email-input" type="email" required value={form.email} onChange={(e) => onChange("email", e.target.value)} placeholder="you@example.com" className="mt-2 rounded-xl bg-mr-bg border-mr h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label>Password</Label>
                <div className="mt-2 relative">
                  <Input
                    data-testid="register-password-input"
                    type={showPw ? "text" : "password"}
                    required
                    minLength={6}
                    value={form.password}
                    onChange={(e) => onChange("password", e.target.value)}
                    placeholder="At least 6 characters"
                    className="rounded-xl bg-mr-bg border-mr h-11 pr-11"
                  />
                  <button
                    type="button"
                    data-testid="register-password-toggle"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mr-secondary hover:text-mr-primary"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>Confirm password</Label>
                <div className="mt-2 relative">
                  <Input
                    data-testid="register-password-confirm-input"
                    type={showConfirm ? "text" : "password"}
                    required
                    minLength={6}
                    value={form.confirm_password}
                    onChange={(e) => onChange("confirm_password", e.target.value)}
                    placeholder="Re-enter your password"
                    className={`rounded-xl bg-mr-bg border-mr h-11 pr-11 ${form.confirm_password && form.password !== form.confirm_password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  />
                  <button
                    type="button"
                    data-testid="register-password-confirm-toggle"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mr-secondary hover:text-mr-primary"
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {form.confirm_password && form.password !== form.confirm_password && (
                  <p className="text-xs text-red-600 mt-1">Passwords don't match</p>
                )}
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="register-phone-input" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="+9715..." className="mt-2 rounded-xl bg-mr-bg border-mr h-11" />
              </div>
              <div>
                <Label>Date of birth</Label>
                <Input data-testid="register-dob-input" type="date" value={form.date_of_birth} onChange={(e) => onChange("date_of_birth", e.target.value)} className="mt-2 rounded-xl bg-mr-bg border-mr h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label>Referral code <span className="text-xs text-mr-secondary">(optional)</span></Label>
                <Input data-testid="register-ref-input" value={form.referral_code} onChange={(e) => onChange("referral_code", e.target.value.toUpperCase())} placeholder="REFXXXXXX" className="mt-2 rounded-xl bg-mr-bg border-mr h-11" />
              </div>
              <div className="sm:col-span-2">
                <Button data-testid="register-submit-btn" type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                  {submitting ? "Sending code…" : "Send verification code"}
                </Button>
              </div>
            </form>

            <div className="flex items-center my-6 gap-3 text-mr-secondary text-xs uppercase tracking-[0.22em]"><span className="flex-1 h-px bg-mr-border" />or<span className="flex-1 h-px bg-mr-border" /></div>

            <Button data-testid="register-google-btn" onClick={handleGoogle} variant="outline" className="w-full h-11 rounded-full border-mr">
              <img alt="Google" className="h-4 w-4 mr-2" src="https://www.google.com/favicon.ico" /> Continue with Google
            </Button>

            <p className="mt-6 text-sm text-mr-secondary text-center">
              Already a member? <Link data-testid="register-login-link" to={`/c/${slug}/login`} className="text-mr-primary font-medium underline underline-offset-4">Sign in</Link>
            </p>
          </>
        )}

        {step === "otp" && (
          <>
            <button data-testid="otp-back-btn" onClick={() => setStep("form")} className="inline-flex items-center gap-1 text-sm text-mr-secondary hover:text-mr-primary mb-6">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-[#E2E8F0] flex items-center justify-center"><MailCheck className="h-5 w-5 text-mr-accent" /></div>
              <h1 className="font-display text-2xl font-semibold text-mr-primary">Check your email</h1>
            </div>
            <p className="text-mr-secondary">We sent a 6-digit code to <b className="text-mr-primary">{form.email}</b>. Enter it below to finish creating your account.</p>

            <div className="mt-8 flex justify-center">
              <InputOTP value={otp} onChange={setOtp} maxLength={6} data-testid="otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-14 w-12 text-2xl font-display font-bold" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button data-testid="otp-verify-btn" onClick={verify} disabled={submitting || otp.length !== 6} className="mt-8 w-full h-11 rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
              {submitting ? "Verifying…" : "Verify & continue"}
            </Button>

            <div className="mt-6 text-center text-sm text-mr-secondary">
              Didn't get it?{" "}
              <button data-testid="otp-resend-btn" onClick={resend} disabled={resendIn > 0} className="text-mr-primary font-medium underline underline-offset-4 disabled:no-underline disabled:text-mr-secondary">
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
